<!-- 購入枚数設定UI -->
<template>
  <section>
    <div class="actions">
      <!-- 規約に同意してチケット内容を確認するボタン -->
      <TheStickyMenu
        :label="$t('TicketStoreTicketing.goToBookingConfirm')"
        :is-submit-disabled="isSubmitDisabled"
        @clicked="createBookGroup"
        @cancelClicked="gotoMasterbook"
      />
    </div>

    <!-- ローディングダイアログ -->
    <DialogLoading ref="DialogLoading" :timeout-seconds="7000" />

    <!-- 販売制限エラーダイアログ -->
    <DialogSaleLimit ref="DialogSaleLimit" :message="saleLimitErrorMessage" />
  </section>
</template>

<script>
import { computed, watchEffect, ref } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";

// API
import API from "@/API";

import moment from "@/services/moment";
import commonService from "@/services/common";
import masterBookService from "@/services/masterBook";
import userInfoService from "@/services/userInfo";

export default {
  name: "TheBbActions",
  props: {
    isRadio: {
      type: Boolean,
      required: true,
    },
    dataDivisions: {
      type: Array,
      required: true,
    },
    isRestrictedMasterBook: {
      type: Boolean,
      required: true,
    },
    selectedStationCode: {
      type: Number,
      required: true,
    },
    isDateLimitedIn: {
      type: Boolean,
      required: true,
    },
    startDateOfUse: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const DialogSaleLimit = ref(null);
    const saleLimitErrorMessage = ref("");

    const DialogLoading = ref(null);
    const masterBook = computed(() => store.getters["ticketing/masterBook"]);
    const isSubscription = computed(() =>
      commonService.checkIsSubscription(route.params.id)
    );
    // 必要な情報が全て入力済みの場合にfalseになる
    const isSubmitDisabled = ref(true);

    const isInvalidDivisions = computed(() => {
      // dataDivision生成前は無効
      if (props.dataDivisions.length === 0) {
        return true;
      }

      var _totalCount = 0;
      var _isOrverMaxPurchaseCount = false;
      props.dataDivisions.forEach((division) => {
        if (division.maxPurchaseCount < division.count) {
          _isOrverMaxPurchaseCount = true;
        }
        _totalCount += division.count;
      });
      return _totalCount === 0 || _isOrverMaxPurchaseCount;
    });

    const checkIsSubmitDisabled = () => {
      // group_gettable_countsの上限に達している場合
      if (props.isRestrictedMasterBook) {
        return true;
      } else if (
        masterBookService.checkCanBuyWithoutAdult(masterBook.value.id)
      ) {
        // 大人必須ではない
        return (
          isInvalidDivisions.value ||
          !props.isDateLimitedIn ||
          props.selectedStationCode == 1
        );
      } else {
        // 大人必須(人数選択がラジオではない場合のみ)
        return (
          isInvalidDivisions.value ||
          (props.dataDivisions[0].count == 0 && !props.isRadio) ||
          !props.isDateLimitedIn ||
          props.selectedStationCode == 1
        );
      }
    };

    // チケット内容を確認するボタンをおして発火する関数
    const createBookGroup = async () => {
      DialogLoading.value.openDialog();
      try {
        await postCreateBookGroup();
      } catch (err) {
        console.log("Error in createBookGroup :", err);
        // 販売制限に関するエラーの場合は、ダイアログ表示
        if (err.response.data.error == "reserved_error") {
          saleLimitErrorMessage.value =
            "この券種・日程は完売したか、販売対象日になっていません。";
          DialogSaleLimit.value.openDialog();
        } else if (err.response.data.error == "sales_error_end_of_sale") {
          saleLimitErrorMessage.value =
            "このチケットの販売期間は終了しました。";
          DialogSaleLimit.value.openDialog();
        } else if (
          err.response.data.error == "sales_error_reservation_expired"
        ) {
          saleLimitErrorMessage.value =
            "仮予約から所定の時間が経過したため、仮予約が取り消されました。もう一度お試しください。";
          DialogSaleLimit.value.openDialog();
        } else if (err.response.data.error == "sales_error_suspended") {
          saleLimitErrorMessage.value =
            "このチケットは現在お取り扱いを中止しております。";
          DialogSaleLimit.value.openDialog();
        }
        return;
      } finally {
        DialogLoading.value.closeDialog();
      }

      setInputValuesToStore();
      gotoBookBookingPaymentSelection();
    };

    const setInputValuesToStore = () => {
      // storeのDivisionsに数値を保持する
      store.commit("input/ticketing/setStoreDivisions", props.dataDivisions);
      // storeにstartDateOfUseを格納
      store.commit(
        "input/ticketing/setStoreStartDateOfUse",
        props.startDateOfUse
      );
      // store selectedStationCode
      store.commit(
        "input/ticketing/setStoreSelectedStationCode",
        props.selectedStationCode
      );
    };

    // Goto payment selection page
    const gotoBookBookingPaymentSelection = () => {
      store.commit("input/ticketing/setStorePaymentMethod", null);
      router.push({
        name: "BookBookingPaymentSelection",
        params: {
          id: route.params.id,
          book_group_id: store.getters["ticketing/bookGroup"].id,
        },
        query: {
          language: store.getters["common/lang"],
          is_0yen_ticket: route.query.is_0yen_ticket,
          master_book_set_id: route.query.master_book_set_id,
          // フリーパス(id:45)用のクエリなので、必要なくなれば削除
          // stationとは？ 不要？
          // station: this.station
        },
      });
    };

    // Masterbookへ戻るが、route単位で行うこと
    // Androidの端末の戻るボタンが影響してしまうため
    const gotoMasterbook = () => {
      router.go(-1);
      // masterBookService.goToBook(masterBook.value, router);
    };

    // bookGroup作成APIコール
    const postCreateBookGroup = async () => {
      // リクエストボディを作成
      var apiParams = makeApiParams();

      return await API.createBookGroup(apiParams);
    };

    // リクエストボディを作成
    const makeApiParams = () => {
      // リクエスト用のapiParamsをObjectで初期化
      var apiParams = {};

      // POST /createBookGroup でリクエストに載せるuser_idをidTokenから抽出する
      var userId = userInfoService.getUserIdFromIdToken();

      // POST /createBookGroup でリクエストに載せるunitを整える
      var unit = arrangeUnit();

      // POST /createBookGroup でリクエストに載せるstartDayを整える
      var startDay = arrangeStartDay();

      // POST /createBookGroup のリクエストボディを作成
      apiParams = {
        master_book_id: masterBook.value.id,
        user_id: userId,
        started_at: startDay,
        // 以下8つの枠をAPI parametersとして整形するが、division==null の時は無になるのでOK
        unit: unit,
      };
      return apiParams;
    };

    //リクエストに載せるunitを整える
    const arrangeUnit = () => {
      // POST /createBookGroup で囲繞するunitを整える
      var unit = [];
      // Emotパスポートはcount: 1 のみ
      if (isSubscription.value) {
        console.log("subscription !!!");
        unit = [{ count: Number(props.dataDivisions[0].count) }];
      } else {
        props.dataDivisions.map((d) =>
          unit.push({
            division: d.dataName,
            count: Number(d.count),
            from_station_code: props.selectedStationCode,
          })
        );
      }
      return unit;
    };

    //リクエストに載せるstartDayを整える
    const arrangeStartDay = () => {
      // is_readyがfalseの場合、日付指定せず、null
      var startDay = null;
      if (masterBook.value.is_ready) {
        startDay = moment(new Date(props.startDateOfUse)).format();
      }
      return startDay;
    };

    watchEffect(() => {
      isSubmitDisabled.value = checkIsSubmitDisabled();
    });

    return {
      isSubmitDisabled,
      createBookGroup,
      gotoMasterbook,
      DialogLoading,
      DialogSaleLimit,
      saleLimitErrorMessage,
    };
  },
};
</script>

<style lang="scss" scoped></style>
